import { IBaseRequestParams, IRequestParams, type Headers } from '../models/types';
import { HttpMethod } from '../models/enums';
import { RestApiError } from '../utils';

export const baseURL = process.env.API_URL;

export class Api {
  private static readonly baseURL = process.env.API_URL;

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    return this._request({
      method: HttpMethod.Get,
      url: request.url,
      signal: request.signal,
      responseType: request.responseType,
    });
  }

  protected static post<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._request<Request, Response>({
      method: HttpMethod.Post,
      url: request.url,
      data: request.data,
      signal: request.signal,
    });
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._request<Request, Response>({
      method: HttpMethod.Put,
      url: request.url,
      data: request.data,
      signal: request.signal,
    });
  }

  protected static delete<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._request<Request, Response>({
      method: HttpMethod.Delete,
      url: request.url,
      data: request.data,
      signal: request.signal,
    });
  }

  private static async _request<Request, Response>(request: IBaseRequestParams<Request>): Promise<Response> {
    const requestId = crypto.randomUUID();
    const headers =
      request.data && request.data instanceof FormData
        ? { ...(request.headers || {}) }
        : ({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Request-Id': requestId,
            insigniatoken: '8B70C342-D152-4C5E-8AF6-F9581DFB4704',
            ...(request.headers || {}),
          } as Headers);

    const options = {
      method: request.method || 'GET',
      body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
      headers,
      signal: request.signal,
      responseType: request.responseType,
    };

    const response = await fetch(this.baseURL + request.url, options);

    if (response.ok) {
      const contentType = response.headers.get('content-type');

      if (response.status === 204) return response.status as Response;
      if (contentType && contentType.indexOf('application/json') !== -1) return response.json();
      return {
        data: await response.arrayBuffer(),
        contentType: contentType,
        contentDisposition: response.headers.get('content-disposition'),
      } as Response;
    } else {
      const data = await response.json();

      const error = new RestApiError(data.message || 'Unknown error', response.status, data, request.method, request.url);

      throw error;
    }
  }
}
